//src/pages/questions/ViewQuestionnairePage.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getAssessmentDetails } from "../../api/questions/questionsApi";
import MyAssessmentLogo from "../../assets/images/myassessment.ai_logo.png";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { IoMdArrowRoundBack } from "react-icons/io";
import { RiDownload2Fill } from "react-icons/ri";
pdfMake.vfs = pdfFonts.vfs;

const ViewQuestionnairePage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState({
    preAssessment: null,
    postAssessment: null,
  });
  const [selectedAssessment, setSelectedAssessment] = useState("Post");
  const [base64Image, setBase64Image] = useState(null);

  useEffect(() => {
    const convertImageToBase64 = async () => {
      try {
        const response = await fetch(MyAssessmentLogo);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => setBase64Image(reader.result);
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error("Error converting image to Base64:", error);
      }
    };
    convertImageToBase64();
  }, []);

  const backButtonHandler = () => navigate("/projects");

  useEffect(() => {
    const fetchAssessments = async () => {
      try {
        const data = await getAssessmentDetails(projectId);
        setAssessments(data);
      } catch (error) {
        console.error("Error fetching assessment details:", error.message);
        alert("Failed to fetch assessment details.");
      }
    };
    fetchAssessments();
  }, [projectId]);

  const currentAssessment =
    selectedAssessment === "Pre"
      ? assessments.preAssessment
      : assessments.postAssessment;

  if (!assessments.postAssessment) {
    return <div>No assessments available for this project.</div>;
  }

  const generatePDF = () => {
    if (!base64Image) { alert("Image is still loading. Please try again."); return; }
    if (!currentAssessment) { alert("No assessment data available."); return; }

    const {
      difficulty_level, selected_sections, total_time,
      subjective_questions, objective_questions, anchor_question,
      total_marks_per_section, marks_per_question, question_code,
    } = currentAssessment;

    const docDefinition = {
      content: [
        { columns: [{ image: base64Image, width: 160, height: 50 }, { text: "Assessment Questionnaire", style: "subheader", alignment: "right", margin: [5, 0] }] },
        { columns: [{ text: `Assessment Code: ${question_code}`, style: "subheader" }, { text: `Difficulty Level: ${difficulty_level}`, style: "subheader", alignment: "right" }], margin: [0, 5] },
        { columns: [{ text: `Total Time: ${total_time} minutes`, style: "infoText" }, { text: `Total Marks: ${total_marks_per_section.subjective + total_marks_per_section.objective + total_marks_per_section.conversational}`, style: "infoText", alignment: "right" }], margin: [0, 5] },
        { canvas: [{ type: "line", x1: 0, y1: 10, x2: 515, y2: 10, lineWidth: 1 }] },
        selected_sections.subjective && subjective_questions?.length > 0 ? { columns: [{ text: "\nSubjective Questions:", style: "sectionHeader" }, { text: `Total: ${total_marks_per_section.subjective} marks`, style: "infoText", alignment: "right", margin: [0, 15, 0, 0] }] } : null,
        ...subjective_questions.map((q, index) => ({ columns: [{ text: `${index + 1}. ${q.question_text}`, style: "questionText", width: "85%" }, { text: `[${marks_per_question.subjective} marks]`, style: "infoText", alignment: "right", width: "15%" }], style: "questionText" })),
        { canvas: [{ type: "line", x1: 0, y1: 10, x2: 515, y2: 10, lineWidth: 1 }] },
        selected_sections.objective && objective_questions?.length > 0 ? { columns: [{ text: "\nObjective Questions:", style: "sectionHeader" }, { text: `Total: ${total_marks_per_section.objective} marks`, style: "infoText", alignment: "right", margin: [0, 15, 0, 0] }] } : null,
        ...objective_questions.map((q, index) => [
          { columns: [{ text: `${index + 1}. ${q.question_text}`, style: "questionText", width: "85%" }, { text: `[${marks_per_question.objective} marks]`, style: "infoText", alignment: "right", width: "15%" }] },
          { table: { widths: ["50%", "50%"], body: [[{ text: `A. ${q.options[0]}`, style: "optionBox", margin: [5, 5], fillColor: "#E3F2FD", borderRadius: 5 }, { text: `B. ${q.options[1]}`, style: "optionBox", margin: [5, 5], fillColor: "#E3F2FD", borderRadius: 5 }], [{ text: `C. ${q.options[2]}`, style: "optionBox", margin: [5, 5], fillColor: "#E3F2FD", borderRadius: 5 }, { text: `D. ${q.options[3]}`, style: "optionBox", margin: [5, 5], fillColor: "#E3F2FD", borderRadius: 5 }]] }, margin: [20, 10] },
        ]),
        { canvas: [{ type: "line", x1: 0, y1: 10, x2: 515, y2: 10, lineWidth: 1 }] },
        selected_sections.conversational_ai ? { columns: [{ text: "\nConversational AI Questions:", style: "sectionHeader" }, { text: `Total: ${total_marks_per_section.conversational} marks`, style: "infoText", alignment: "right", margin: [0, 15, 0, 0] }] } : null,
        selected_sections.conversational_ai && anchor_question ? { columns: [{ text: `Anchor Question: ${anchor_question}`, style: "questionText", width: "85%" }, { text: `[${marks_per_question.conversational} marks]`, style: "infoText", alignment: "right", width: "15%" }] } : null,
      ],
      styles: {
        header: { fontSize: 22, bold: true, alignment: "center", margin: [0, 10] },
        subheader: { fontSize: 16, bold: true, margin: [0, 5] },
        infoText: { fontSize: 12, margin: [0, 3] },
        sectionHeader: { fontSize: 14, bold: true, color: "#007acc", margin: [0, 3] },
        questionText: { fontSize: 12, margin: [10, 5] },
        optionBox: { fontSize: 12, alignment: "left", margin: [10, 10], padding: [10, 5], borderRadius: "10px" },
      },
    };
    pdfMake.createPdf(docDefinition).download(`${selectedAssessment}_Assessment.pdf`);
  };

  const SectionHeader = ({ title, totalMarks, marksPerQuestion, gradient }) => (
    <div className={`flex justify-between items-center px-6 py-4 ${gradient} text-white`}>
      <h3 className="font-bold text-sm md:text-base lg:text-lg tracking-wide">{title}</h3>
      <div className="flex items-center gap-2 md:gap-6">
        <span className="text-xs md:text-sm font-medium bg-white/20 px-3 py-1 rounded-full whitespace-nowrap">
          Total: <strong>{totalMarks}</strong> marks
        </span>
        <span className="text-xs md:text-sm font-medium bg-white/20 px-3 py-1 rounded-full whitespace-nowrap">
          Per Q: <strong>{marksPerQuestion}</strong>
        </span>
      </div>
    </div>
  );

  const renderAssessmentDetails = (assessment) => {
    if (!assessment) return <div>No details available for this assessment.</div>;

    const {
      selected_sections, total_time, subjective_questions, objective_questions,
      anchor_question, total_marks_per_section, marks_per_question, difficulty_level,
    } = assessment;

    return (
      <>
    
        <div className="lg:w-8/12 w-11/12 mx-auto mt-6 mb-4 flex items-center justify-between gap-4 px-6 py-4 rounded-2xl bg-gradient-to-r from-white/70 to-blue-50/70 backdrop-blur-xl border border-white/40 shadow-lg">
          <div className="flex flex-col items-center flex-1">
            <span className="text-gray-400 text-xs md:text-sm uppercase tracking-widest font-medium">Difficulty</span>
            <span className="text-lg md:text-xl font-bold text-indigo-600 mt-0.5">{difficulty_level}</span>
          </div>
          <div className="hidden md:block h-10 w-px bg-gray-200"></div>
          <div className="flex flex-col items-center flex-1">
            <span className="text-gray-400 text-xs md:text-sm uppercase tracking-widest font-medium">Duration</span>
            <span className="text-lg md:text-xl font-bold text-indigo-600 mt-0.5">{total_time} min</span>
          </div>
          <div className="hidden md:block h-10 w-px bg-gray-200"></div>
          <div className="flex flex-col items-center flex-1">
            <span className="text-gray-400 text-xs md:text-sm uppercase tracking-widest font-medium">Total Marks</span>
            <span className="text-lg md:text-xl font-bold text-indigo-600 mt-0.5">
              {total_marks_per_section.subjective + total_marks_per_section.objective + total_marks_per_section.conversational}
            </span>
          </div>
        </div>

       
        <div className="w-11/12 mx-auto py-6 space-y-6">

       
          {selected_sections.subjective && subjective_questions?.length > 0 && (
            <div className="rounded-2xl bg-white shadow-lg border border-gray-100 overflow-hidden transition-shadow hover:shadow-xl">
              <SectionHeader
                title="Subjective Questions"
                totalMarks={total_marks_per_section.subjective}
                marksPerQuestion={marks_per_question.subjective}
                gradient="bg-[#36369f]"
              />
              <div className="divide-y divide-gray-100">
                {subjective_questions.map((q, index) => (
                  <div key={q.q_id} className="px-6 py-4 hover:bg-purple-50/40 transition-colors duration-150">
                    <p className="text-left text-sm md:text-base text-gray-700 leading-relaxed">
                      <span className="font-bold text-indigo-600 mr-2">{index + 1}.</span>
                      {q.question_text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

        
          {selected_sections.objective && objective_questions?.length > 0 && (
            <div className="rounded-2xl bg-white shadow-lg border border-gray-100 overflow-hidden transition-shadow hover:shadow-xl">
              <SectionHeader
                title="Objective Questions"
                totalMarks={total_marks_per_section.objective}
                marksPerQuestion={marks_per_question.objective}
                gradient="bg-[#36369f]"
              />
              <div className="divide-y divide-gray-100">
                {objective_questions.map((q, index) => (
                  <div key={q.q_id} className="px-6 py-5 hover:bg-indigo-50/30 transition-colors duration-150">
                    <p className="text-sm md:text-base text-gray-700 font-medium leading-relaxed mb-4">
                      <span className="font-bold text-indigo-600 mr-2">{index + 1}.</span>
                      {q.question_text}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3 ml-5">
                      {q.options.map((opt, idx) => (
                        <div
                          key={opt}
                          className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-gray-50 border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 hover:shadow-sm transition-all duration-150 cursor-pointer"
                        >
                          <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-indigo-100 text-indigo-600 font-bold text-xs md:text-sm flex items-center justify-center">
                            {String.fromCharCode(65 + idx)}
                          </span>
                          <p className="text-xs md:text-sm text-gray-700 font-medium">{opt}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

         
          {selected_sections.conversational_ai && (
            <div className="rounded-2xl bg-white shadow-lg border border-gray-100 overflow-hidden transition-shadow hover:shadow-xl">
              <SectionHeader
                title="Conversational AI Questions"
                totalMarks={total_marks_per_section.conversational}
                marksPerQuestion={marks_per_question.conversational}
                gradient="bg-[#36369f]"
              />
              <div className="px-6 py-5 bg-indigo-50/40">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 text-xs font-bold uppercase tracking-widest text-indigo-500 bg-indigo-100 px-3 py-1.5 rounded-lg mt-0.5">
                    Anchor question
                  </span>
                  <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                    {anchor_question || "Not provided"}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="relative w-full min-h-screen bg-white overflow-hidden">

    
      {/* <div className="absolute top-0 left-0 w-96 h-96 bg-purple-300 opacity-15 blur-3xl rounded-full pointer-events-none"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-300 opacity-15 blur-3xl rounded-full pointer-events-none"></div> */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-indigo-200 opacity-10 blur-3xl rounded-full pointer-events-none"></div>

      <div className="relative z-10">
        
        <div className="lg:w-8/12 w-11/12 my-6 mx-auto flex items-center justify-between gap-3 bg-white/70 backdrop-blur-xl shadow-lg rounded-2xl px-4 py-3 border border-white/50">

         
          <button
            onClick={backButtonHandler}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold text-sm shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all duration-200"
          >
            <IoMdArrowRoundBack className="text-lg" />
            <span className="hidden md:block">Back</span>
          </button>

        
          <div className="flex-1 max-w-xs md:max-w-sm mx-auto">
            <div className="relative flex items-center w-full h-11 p-1 rounded-xl bg-gray-100 shadow-inner">
              {/* Sliding pill */}
              <div
                className={`absolute top-1 left-1 h-[calc(100%-8px)] w-[calc(50%-4px)] rounded-lg bg-gradient-to-r from-indigo-500 to-indigo-600 shadow-md transition-transform duration-400 ease-in-out ${selectedAssessment === "Post" ? "translate-x-full" : "translate-x-0"}`}
              ></div>
              <button
                className={`w-full z-10 py-1.5 rounded-lg text-sm font-semibold transition-colors duration-300 ${selectedAssessment === "Pre" ? "text-white" : "text-gray-500 hover:text-indigo-600"}`}
                onClick={() => setSelectedAssessment("Pre")}
                disabled={!assessments.preAssessment}
              >
                Pre Assessment
              </button>
              <button
                className={`w-full z-10 py-1.5 rounded-lg text-sm font-semibold transition-colors duration-300 ${selectedAssessment === "Post" ? "text-white" : "text-gray-500 hover:text-indigo-600"}`}
                onClick={() => setSelectedAssessment("Post")}
              >
                Post Assessment
              </button>
            </div>
          </div>

         
          <button
            onClick={generatePDF}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-blue-500 text-white font-semibold text-sm shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all duration-200"
          >
            <RiDownload2Fill className="text-lg" />
            <span className="hidden md:block whitespace-nowrap">Download {selectedAssessment}</span>
          </button>
        </div>

       
        {renderAssessmentDetails(currentAssessment)}
      </div>
    </div>
  );
};

export default ViewQuestionnairePage;